/**
 * Camera-preset compatibility: what may be captured, and what may safely be
 * written back onto a DIFFERENT camera.
 *
 * This module is pure — no camera, no store, no I/O — so the decisions that
 * make a cross-camera apply safe are unit-testable without hardware. The engine
 * that actually writes lives in `cameraPresets.ts`.
 *
 * Two ideas do most of the work:
 *
 * 1. A libgphoto2 tree is NOT all settings. `/main/actions/` holds VERBS
 *    (eosremoterelease, manualfocusdrive, capture) — writing them fires the
 *    shutter or racks the lens. `/main/status/` is read-only telemetry.
 *    `/main/other/` is raw PTP property codes. Only `/main/imgsettings/` and
 *    `/main/capturesettings/` are captured here; `/main/settings/` is the
 *    booth-baseline layer and is deliberately out of scope for now.
 *
 * 2. Values are model-specific strings. "1/125" on one body may be "125" on
 *    another, and a choice list that lacks the exact string must not be written
 *    blind. So every cross-model write is resolved against the TARGET's own
 *    `choices`/`min`/`max`, and anything that cannot be resolved is reported as
 *    skipped rather than attempted.
 *
 * Design: docs/camera-preset-plan.md §4 and §6.
 */

export type PresetGroup = 'exposure' | 'image' | 'behaviour'

/** How close the preset's source camera is to the one currently connected. */
export type MatchTier = 'exact' | 'brand' | 'cross'

export interface PresetEntry {
  /** libgphoto2 leaf widget name — the write key. */
  name: string
  /** Full tree path, for diagnostics and same-name disambiguation. */
  path?: string
  type: string
  value: string | number | boolean
  label?: string
  group: PresetGroup
}

export interface ConfigNode {
  name: string
  label?: string
  info?: string
  readonly?: boolean
  type?: string
  value?: string | number | boolean
  choices?: string[]
  min?: number
  max?: number
  step?: number
  children?: Record<string, ConfigNode>
}

/* ────────────────────────────── denylist ────────────────────────────── */

/**
 * Never captured, never written. Mirrored server-side in
 * `dreambooth/lib/cameraPresetSanitize.ts` — the server is authoritative
 * because a preset is replayed onto other people's hardware, but filtering here
 * too keeps junk out of the payload and out of the operator's plan preview.
 */
const DENIED_WIDGETS = new Set([
  // /main/actions/ — verbs, not settings
  'capture',
  'bulb',
  'uilock',
  'opcode',
  'syncdatetime',
  'syncdatetimeutc',
  'eosremoterelease',
  'autofocusdrive',
  'manualfocusdrive',
  'focusdrive',
  'eoszoom',
  'eoszoomposition',
  'movierecordtarget',
  // Owned by Camera Rest (flag `camera_rest`). A preset must never fight it.
  'viewfinder',
  'eosviewfinder',
  // Clock
  'datetime',
  'datetimeutc',
  // Per-owner metadata
  'ownername',
  'artist',
  'copyright',
  // /main/status/ — read-only
  'cameramodel',
  'model',
  'manufacturer',
  'deviceversion',
  'serialnumber',
  'eosserialnumber',
  'shuttercounter',
  'availableshots',
  'batterylevel',
  'lensname',
  'vendorextension'
])

export function isDeniedWidget(name: string): boolean {
  const lower = name.toLowerCase()
  return DENIED_WIDGETS.has(lower) || lower.startsWith('0x')
}

/* ───────────────────────────── grouping ─────────────────────────────── */

/** Widgets in /main/capturesettings/ that are exposure rather than behaviour. */
const EXPOSURE_WIDGETS = new Set([
  'autoexposuremode',
  'autoexposuremodedial',
  'expprogram',
  'exposureprogram',
  'shutterspeed',
  'shutterspeed2',
  'shutterspeedvalue',
  'aperture',
  'f-number',
  'fnumber',
  'iso',
  'exposurecompensation',
  'exposurebiascompensation',
  'meteringmode',
  'exposuremetermode'
])

/**
 * Which apply-group a widget belongs to, or null when it must not be captured.
 * Decided from the PATH (the libgphoto2 section) rather than the name, because
 * section membership is what actually tells settings apart from verbs.
 */
export function classifyGroup(path: string, name: string): PresetGroup | null {
  if (isDeniedWidget(name)) return null
  const p = path.toLowerCase()
  if (p.includes('/imgsettings/')) return 'image'
  if (p.includes('/capturesettings/')) {
    return EXPOSURE_WIDGETS.has(name.toLowerCase()) ? 'exposure' : 'behaviour'
  }
  // /main/settings/ is the booth-baseline layer (autopoweroff, capturetarget,
  // reviewtime) — a different mechanism with a different error policy, applied
  // automatically rather than by operator choice. Out of scope here.
  return null
}

/* ─────────────────────────── tree traversal ─────────────────────────── */

export interface FlatNode {
  node: ConfigNode
  path: string
}

/** Depth-first walk yielding every node with its `/main/...`-style path. */
export function flattenWithPaths(
  root: ConfigNode | null | undefined,
  basePath = ''
): FlatNode[] {
  if (!root || typeof root !== 'object' || typeof root.name !== 'string') return []
  const path = `${basePath}/${root.name}`
  const out: FlatNode[] = [{ node: root, path }]
  if (root.children) {
    for (const child of Object.values(root.children)) {
      out.push(...flattenWithPaths(child, path))
    }
  }
  return out
}

/** Leaf name → node, for resolving a preset entry against a target camera. */
export function indexByName(root: ConfigNode | null | undefined): Map<string, ConfigNode> {
  const index = new Map<string, ConfigNode>()
  for (const { node } of flattenWithPaths(root)) {
    // First writer wins: libgphoto2 trees can repeat a name across sections
    // (imageformat / imageformatsd); the first is the one gphoto2 itself
    // resolves with gp_widget_get_child_by_name.
    if (!index.has(node.name)) index.set(node.name, node)
  }
  return index
}

const CONTAINER_TYPES = new Set(['window', 'section'])

/**
 * Capture every writable, in-scope widget from a config tree.
 *
 * `overriddenValues` exists for one specific hazard: `applyLiveViewBrightness`
 * overrides `shutterspeed` on the live camera whenever the operator has the
 * brighten-for-flash toggle on, restoring it only around capture. Snapshotting
 * in that window would record 1/30 as the operator's chosen exposure and then
 * propagate it to every other booth — so callers pass the saved ORIGINAL values
 * and they win over what the camera currently reports.
 */
export function captureEntriesFromConfig(
  root: ConfigNode | null | undefined,
  overriddenValues: Record<string, string | number | boolean> = {}
): PresetEntry[] {
  const entries: PresetEntry[] = []
  const seen = new Set<string>()

  for (const { node, path } of flattenWithPaths(root)) {
    if (!node.name || seen.has(node.name)) continue
    if (node.readonly) continue
    if (node.type && CONTAINER_TYPES.has(node.type)) continue
    if (node.type === 'datetime') continue
    if (node.value === undefined || node.value === null) continue

    const group = classifyGroup(path, node.name)
    if (!group) continue

    seen.add(node.name)
    const override = Object.prototype.hasOwnProperty.call(overriddenValues, node.name)
    entries.push({
      name: node.name,
      path,
      type: node.type || 'text',
      value: override ? overriddenValues[node.name] : node.value,
      label: node.label,
      group
    })
  }

  return entries
}

/* ──────────────────────── camera identity + tier ─────────────────────── */

export type CameraBrand = 'canon' | 'nikon' | 'sony' | 'unknown'

/** Model string as the camera reports it, e.g. "Canon EOS R100". */
export function readCameraModel(root: ConfigNode | null | undefined): string {
  for (const { node } of flattenWithPaths(root)) {
    if (/^(cameramodel|model)$/i.test(node.name) && typeof node.value === 'string') {
      const value = node.value.trim()
      if (value) return value
    }
  }
  return ''
}

function normaliseModel(model: string): string {
  return model
    .toLowerCase()
    .replace(/\b(canon|nikon|sony|eos|corporation|inc\.?)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, '')
    .trim()
}

/**
 * How compatible a stored preset is with the connected camera.
 *
 * Deliberately generous about "exact": two bodies of the same model report the
 * same string, which is the common multi-booth case this feature exists for.
 */
export function detectMatchTier(
  presetModel: string,
  presetBrand: CameraBrand,
  targetModel: string,
  targetBrand: CameraBrand
): MatchTier {
  const a = normaliseModel(presetModel)
  const b = normaliseModel(targetModel)
  if (a && b && a === b) return 'exact'
  if (
    presetBrand !== 'unknown' &&
    targetBrand !== 'unknown' &&
    presetBrand === targetBrand
  ) {
    return 'brand'
  }
  return 'cross'
}

/* ─────────────────────── value normalisation ─────────────────────────── */

/**
 * Shutter speed as seconds. Cameras report "1/125", "125" (Sony), "0.5", "2",
 * or "2s"; "bulb"/"auto" have no numeric meaning and return null so the caller
 * skips rather than guesses.
 */
export function parseShutterSeconds(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : null
  if (typeof raw !== 'string') return null
  const value = raw.trim().toLowerCase()
  if (!value || /bulb|auto|unknown|invalid/.test(value)) return null

  const fraction = value.match(/^1\s*\/\s*(\d+(?:\.\d+)?)$/)
  if (fraction) {
    const denominator = Number(fraction[1])
    return denominator > 0 ? 1 / denominator : null
  }

  const seconds = value.match(/^(\d+(?:\.\d+)?)\s*(?:s|")?$/)
  if (seconds) {
    const n = Number(seconds[1])
    if (!Number.isFinite(n) || n <= 0) return null
    // A bare integer >= 4 on a body that lists "125" style speeds means 1/125,
    // not 125 seconds: no camera offers a 125-second shutter in its menu, and
    // the slowest common value is 30s.
    return n > 30 ? 1 / n : n
  }

  return null
}

/** f-number from "5.6", "f/5.6", "F5.6". */
export function parseAperture(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : null
  if (typeof raw !== 'string') return null
  const match = raw.trim().toLowerCase().replace(/^f\/?/, '').match(/^(\d+(?:\.\d+)?)$/)
  if (!match) return null
  const n = Number(match[1])
  return Number.isFinite(n) && n > 0 ? n : null
}

/** ISO as a number; "Auto" (and friends) return null — not comparable. */
export function parseIso(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : null
  if (typeof raw !== 'string') return null
  const match = raw.trim().replace(/[^\d]/g, '')
  if (!match) return null
  const n = Number(match)
  return Number.isFinite(n) && n > 0 ? n : null
}

const WB_TOKENS: Array<[RegExp, string]> = [
  [/auto/i, 'auto'],
  [/daylight|sunny|sunlight/i, 'daylight'],
  [/cloud/i, 'cloudy'],
  [/shade/i, 'shade'],
  [/tungsten|incandescent/i, 'tungsten'],
  [/fluor/i, 'fluorescent'],
  [/flash|speedlight|strobe/i, 'flash'],
  [/kelvin|color\s*temp|colour\s*temp/i, 'kelvin'],
  [/custom|manual|preset/i, 'custom']
]

/** Reduce a white-balance label to a brand-independent token. */
export function normaliseWhiteBalance(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  for (const [pattern, token] of WB_TOKENS) {
    if (pattern.test(raw)) return token
  }
  return null
}

const EXPOSURE_MODE_TOKENS: Array<[RegExp, string]> = [
  [/^m$|manual/i, 'M'],
  [/^av$|aperture\s*priority|^a$/i, 'Av'],
  [/^tv$|shutter\s*priority|^s$/i, 'Tv'],
  [/^p$|program/i, 'P'],
  [/auto/i, 'Auto']
]

export function normaliseExposureMode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  for (const [pattern, token] of EXPOSURE_MODE_TOKENS) {
    if (pattern.test(raw.trim())) return token
  }
  return null
}

/* ───────────────────────── semantic concepts ────────────────────────── */

type ConceptKey = 'shutter' | 'aperture' | 'iso' | 'whitebalance' | 'expmode' | 'format'

interface Concept {
  /** Widget names that express this concept across brands. */
  names: string[]
  /** Numeric reading, for nearest-value matching. Null = not numeric. */
  numeric?: (raw: unknown) => number | null
  /** Token reading, for enum matching. */
  token?: (raw: unknown) => string | null
  /**
   * Logarithmic quantities (shutter, ISO) — "nearest" must be measured as a
   * ratio, not a difference, or 1/4000 and 1/2000 look adjacent while 1s and 2s
   * look a full second apart.
   */
  logScale?: boolean
}

const CONCEPTS: Record<ConceptKey, Concept> = {
  shutter: {
    names: ['shutterspeed', 'shutterspeed2', 'shutterspeedvalue'],
    numeric: parseShutterSeconds,
    logScale: true
  },
  aperture: {
    names: ['aperture', 'f-number', 'fnumber'],
    numeric: parseAperture,
    logScale: true
  },
  iso: {
    names: ['iso'],
    numeric: parseIso,
    token: (raw) => (typeof raw === 'string' && /auto/i.test(raw) ? 'auto' : null),
    logScale: true
  },
  whitebalance: {
    names: ['whitebalance'],
    token: normaliseWhiteBalance
  },
  expmode: {
    names: ['autoexposuremode', 'autoexposuremodedial', 'expprogram', 'exposureprogram'],
    token: normaliseExposureMode
  },
  format: {
    names: ['imageformat', 'imagequality'],
    token: (raw) => {
      if (typeof raw !== 'string') return null
      // Reuse the vocabulary captureRiskAssessment already classifies by.
      const raw_ = raw.toLowerCase()
      const isRaw = /\braw\b|\bnef\b|\bcraw\b/.test(raw_)
      const size = /small|\bs[123]?\b/.test(raw_)
        ? 'S'
        : /medium|\bm\b/.test(raw_)
          ? 'M'
          : /large|\bl\b/.test(raw_)
            ? 'L'
            : '?'
      const quality = /fine|superfine|extra/.test(raw_) ? 'fine' : /normal|basic|standard/.test(raw_) ? 'normal' : '?'
      return `${isRaw ? 'raw' : 'jpeg'}:${size}:${quality}`
    }
  }
}

function conceptFor(widgetName: string): ConceptKey | null {
  const lower = widgetName.toLowerCase()
  for (const [key, concept] of Object.entries(CONCEPTS) as Array<[ConceptKey, Concept]>) {
    if (concept.names.includes(lower)) return key
  }
  return null
}

/**
 * Pick the target choice closest to a desired value.
 * Returns null when nothing is comparable — the caller then SKIPS rather than
 * writing an arbitrary neighbour.
 */
export function nearestChoice(
  desired: string | number | boolean,
  choices: string[],
  concept: Concept
): string | null {
  if (choices.length === 0) return null

  // Exact string match always wins.
  const desiredStr = String(desired)
  const exact = choices.find((c) => c === desiredStr)
  if (exact !== undefined) return exact

  // Token equality (white balance, exposure mode, image format, ISO "Auto").
  if (concept.token) {
    const wanted = concept.token(desired)
    if (wanted) {
      const match = choices.find((c) => concept.token?.(c) === wanted)
      if (match !== undefined) return match
    }
  }

  // Nearest numeric neighbour.
  if (concept.numeric) {
    const wanted = concept.numeric(desired)
    if (wanted !== null) {
      let best: string | null = null
      let bestDistance = Infinity
      for (const choice of choices) {
        const value = concept.numeric(choice)
        if (value === null) continue
        const distance = concept.logScale
          ? Math.abs(Math.log(value) - Math.log(wanted))
          : Math.abs(value - wanted)
        if (distance < bestDistance) {
          bestDistance = distance
          best = choice
        }
      }
      return best
    }
  }

  return null
}

/** Snap a numeric value into a range widget's own bounds and step. */
export function clampToRange(value: number, node: ConfigNode): number {
  let next = value
  if (typeof node.min === 'number' && next < node.min) next = node.min
  if (typeof node.max === 'number' && next > node.max) next = node.max
  if (typeof node.step === 'number' && node.step > 0 && typeof node.min === 'number') {
    const steps = Math.round((next - node.min) / node.step)
    next = node.min + steps * node.step
    if (typeof node.max === 'number' && next > node.max) next = node.max
  }
  return next
}

/* ────────────────────────── resolution ──────────────────────────────── */

export type SkipReason =
  | 'missing-on-target'
  | 'readonly'
  | 'no-matching-choice'
  | 'mode-locked'
  | 'group-disabled'
  | 'denied'
  | 'cross-model-disabled'

export interface ResolvedWrite {
  entry: PresetEntry
  /** The widget name to write on the TARGET — may differ from entry.name. */
  targetName: string
  value: string | number | boolean
  /** True when the value had to be changed to fit the target. */
  adjusted: boolean
  /** Present when adjusted — what the preset asked for. */
  requested?: string | number | boolean
  /**
   * What the camera held BEFORE this write. Populated by `buildApplyPlan`,
   * which is the only place the target's tree is in scope, and it is what an
   * undo puts back — the preset's own value would just re-apply the preset.
   */
  previousValue?: string | number | boolean
}

export interface SkippedWrite {
  entry: PresetEntry
  reason: SkipReason
}

/**
 * Resolve one preset entry against the connected camera.
 *
 * Name match first (correct for exact and same-brand targets, where widget
 * names agree); concept match second, which is what makes a cross-brand apply
 * possible at all.
 */
export function resolveEntry(
  entry: PresetEntry,
  targetIndex: Map<string, ConfigNode>
): ResolvedWrite | SkippedWrite {
  if (isDeniedWidget(entry.name)) return { entry, reason: 'denied' }

  let targetName = entry.name
  let node = targetIndex.get(entry.name)

  const conceptKey = conceptFor(entry.name)
  if (!node && conceptKey) {
    for (const candidate of CONCEPTS[conceptKey].names) {
      const found = targetIndex.get(candidate)
      if (found) {
        node = found
        targetName = candidate
        break
      }
    }
  }

  if (!node) return { entry, reason: 'missing-on-target' }
  if (node.readonly) return { entry, reason: 'readonly' }

  // Range widgets: clamp into the target's own bounds.
  if (node.type === 'range') {
    const numeric =
      typeof entry.value === 'number' ? entry.value : Number(entry.value)
    if (!Number.isFinite(numeric)) return { entry, reason: 'no-matching-choice' }
    const clamped = clampToRange(numeric, node)
    return {
      entry,
      targetName,
      value: clamped,
      adjusted: clamped !== numeric,
      requested: clamped !== numeric ? entry.value : undefined
    }
  }

  if (node.type === 'toggle') {
    const asBoolean =
      typeof entry.value === 'boolean'
        ? entry.value
        : entry.value === 1 || entry.value === '1' || /^(true|on)$/i.test(String(entry.value))
    return { entry, targetName, value: asBoolean, adjusted: false }
  }

  // Enumerated widgets: the value MUST be one the target actually offers.
  if (Array.isArray(node.choices) && node.choices.length > 0) {
    const concept = conceptKey ? CONCEPTS[conceptKey] : { names: [] }
    const picked = nearestChoice(entry.value, node.choices, concept)
    if (picked === null) return { entry, reason: 'no-matching-choice' }
    return {
      entry,
      targetName,
      value: picked,
      adjusted: picked !== String(entry.value),
      requested: picked !== String(entry.value) ? entry.value : undefined
    }
  }

  // Free text — nothing to validate against.
  return { entry, targetName, value: entry.value, adjusted: false }
}

/* ──────────────────────────── the plan ──────────────────────────────── */

export interface ApplyPlanOptions {
  /** Groups the operator has enabled. Anything else is reported as skipped. */
  enabledGroups: PresetGroup[]
  /**
   * Tier gate. When `camera_preset_cross_model` is off, only exact-model
   * presets may be applied and everything else is refused up front.
   */
  allowCrossModel: boolean
}

export interface ApplyPlan {
  tier: MatchTier
  /** Ordered — exposure mode leads (see below). */
  writes: ResolvedWrite[]
  skipped: SkippedWrite[]
  /** Writes already satisfied by the camera's current value; never sent. */
  noOps: ResolvedWrite[]
}

/** Widgets that only exist in the more manual exposure modes. */
const MODE_DEPENDENT = new Set([
  'shutterspeed',
  'shutterspeed2',
  'shutterspeedvalue',
  'aperture',
  'f-number',
  'fnumber'
])

function isExposureModeWidget(name: string): boolean {
  return CONCEPTS.expmode.names.includes(name.toLowerCase())
}

/**
 * Work out exactly what applying this preset would do, before anything is
 * written. The operator confirms this, so it must be honest about what will be
 * changed, what will be approximated, and what will not happen at all.
 */
export function buildApplyPlan(
  entries: PresetEntry[],
  targetConfig: ConfigNode | null | undefined,
  tier: MatchTier,
  options: ApplyPlanOptions
): ApplyPlan {
  const targetIndex = indexByName(targetConfig)
  const writes: ResolvedWrite[] = []
  const skipped: SkippedWrite[] = []
  const noOps: ResolvedWrite[] = []

  // Tier gate. With `camera_preset_cross_model` off, only an exact-model preset
  // may be written at all — every entry is reported as skipped rather than
  // quietly producing an empty plan, so the UI can say why nothing will happen.
  if (tier !== 'exact' && !options.allowCrossModel) {
    return {
      tier,
      writes: [],
      noOps: [],
      skipped: entries.map((entry) => ({ entry, reason: 'cross-model-disabled' as SkipReason }))
    }
  }

  const enabled = new Set(options.enabledGroups)

  for (const entry of entries) {
    if (!enabled.has(entry.group)) {
      skipped.push({ entry, reason: 'group-disabled' })
      continue
    }
    const resolved = resolveEntry(entry, targetIndex)
    if ('reason' in resolved) {
      skipped.push(resolved)
      continue
    }
    // Skip no-op writes. This is the single biggest cost saver: both backends
    // impose an 800ms settle per write, so on a same-model booth — where most
    // values already agree — this is the difference between a ~20s apply and a
    // ~3s one, and every write not sent is a PTP round trip that cannot fail.
    const current = targetIndex.get(resolved.targetName)?.value
    if (current !== undefined && String(current) === String(resolved.value)) {
      noOps.push(resolved)
      continue
    }
    // Carry the pre-write value so an undo has the real prior state to restore.
    writes.push({ ...resolved, previousValue: current })
  }

  // Exposure mode must be written FIRST: shutter speed and aperture are not
  // settable in Auto or P on Canon, so a preset that switches the body into M
  // has to do that before the values that depend on it.
  writes.sort((a, b) => {
    const aMode = isExposureModeWidget(a.targetName) ? 0 : 1
    const bMode = isExposureModeWidget(b.targetName) ? 0 : 1
    return aMode - bMode
  })

  // If the body's exposure mode is fixed by a physical dial (readonly over PTP)
  // and disagrees with the preset, the mode-dependent writes will be rejected
  // by the camera. Say so up front instead of letting them fail one by one.
  const modeWidget = CONCEPTS.expmode.names
    .map((n) => targetIndex.get(n))
    .find((n): n is ConfigNode => Boolean(n))
  if (modeWidget?.readonly) {
    const presetMode = entries.find((e) => isExposureModeWidget(e.name))?.value
    const wanted = normaliseExposureMode(presetMode)
    const actual = normaliseExposureMode(modeWidget.value)
    if (wanted && actual && wanted !== actual) {
      for (let i = writes.length - 1; i >= 0; i--) {
        if (MODE_DEPENDENT.has(writes[i].targetName.toLowerCase())) {
          skipped.push({ entry: writes[i].entry, reason: 'mode-locked' })
          writes.splice(i, 1)
        }
      }
    }
  }

  return { tier, writes, skipped, noOps }
}
